import React, { useState, useCallback, useMemo, memo } from "react";
import { Grid } from "@mui/material";
import { FormField, RadioButtonGroup } from "../shared/form";
import { MisdeliveryFormData } from "../../models/misDelivery";
import { checkPro } from "../../API/checkPro";
import { userTerminal } from "../../helpers";
import { useMisdeliveryFormContext } from "../../contexts/MisdeliveryFormContext";

// Component no longer needs props - uses context
interface BasicInformationSectionProps {}

// Constants
const FIELD_GRID_PROPS = { md: 3, xs: 12 } as const;
const RADIO_GRID_PROPS = { md: 6, xs: 12 } as const;
const INVALID_PRO_ERROR = "Invalid PRO number" as const;

const HAS_PRO_OPTIONS = ["Y", "N"] as const;

// Fields to clear when PRO validation fails
const FIELDS_TO_CLEAR: ReadonlyArray<keyof MisdeliveryFormData> = [
  "destinationTerminal",
  "originTerminal",
  "lastConfirmedManifest",
  "misdeliveryContactName",
  "misdeliveryAddress1",
  "misdeliveryAddress2",
  "misdeliveryCity",
  "misdeliveryState",
  "misdeliveryZip",
] as const;

// Helper: Check if PRO response is valid
const isValidProResponse = (response: any): boolean => {
  return !!response && Object.keys(response).length > 0;
};

// Helper: Map API response to form fields
const mapProResponseToFields = (
  response: any,
  formData: MisdeliveryFormData,
): Partial<MisdeliveryFormData> => {
  const {
    origin,
    destination,
    manifestNumber,
    consignee,
    consigneeAddress,
    consigneeAddress2,
    consigneeCity,
    consigneeState,
    consigneeZip,
  } = response;

  const isDifferentLocation =
    formData.currentLocationDifferentThanMsdPoint === "N";

  const locationFields = isDifferentLocation
    ? {
        currentLocationAddress1: consigneeAddress,
        currentLocationAddress2: consigneeAddress2,
        currentLocationCity: consigneeCity,
        currentLocationState: consigneeState,
        currentLocationZip: consigneeZip,
      }
    : {
        currentLocationAddress1: formData.currentLocationAddress1,
        currentLocationAddress2: formData.currentLocationAddress2,
        currentLocationCity: formData.currentLocationCity,
        currentLocationState: formData.currentLocationState,
        currentLocationZip: formData.currentLocationZip,
      };

  return {
    originTerminal: origin,
    destinationTerminal: destination,
    lastConfirmedManifest: manifestNumber,
    misdeliveryContactName: consignee,
    misdeliveryAddress1: consigneeAddress,
    misdeliveryAddress2: consigneeAddress2,
    misdeliveryCity: consigneeCity,
    misdeliveryState: consigneeState,
    misdeliveryZip: consigneeZip,
    pickupTerminal: destination,
    ...locationFields,
  };
};

// Helper: Update multiple form fields
const updateFormFields = (
  fieldMappings: Partial<MisdeliveryFormData>,
  onFieldChange: (field: keyof MisdeliveryFormData, value: any) => void,
) => {
  Object.entries(fieldMappings).forEach(([field, value]) => {
    onFieldChange(field as keyof MisdeliveryFormData, value);
  });
};

// Helper: Get PRO field error state
const getProFieldError = (
  touched: boolean | undefined,
  error: string | undefined,
  proCheckDError: boolean,
): boolean => {
  return (touched && !!error) || proCheckDError;
};

// Helper: Get PRO field helper text
const getProFieldHelperText = (
  touched: boolean | undefined,
  error: string | undefined,
  proCheckDError: boolean,
): string | undefined => {
  if (proCheckDError) return INVALID_PRO_ERROR;
  if (touched) return error;
  return undefined;
};

// Helper: Process PRO validation response
const processProValidation = async (
  pro: string,
  formData: MisdeliveryFormData,
  onFieldChange: (field: keyof MisdeliveryFormData, value: any) => void,
  clearFields: () => void,
  setProCheckDError: (error: boolean) => void,
): Promise<void> => {
  try {
    const checkProResponse = await checkPro(pro);

    if (!isValidProResponse(checkProResponse)) {
      clearFields();
      setProCheckDError(true);
      return;
    }

    setProCheckDError(false);
    const fieldMappings = mapProResponseToFields(checkProResponse, formData);
    updateFormFields(fieldMappings, onFieldChange);
  } catch (error) {
    clearFields();
    setProCheckDError(true);

    if (process.env.NODE_ENV === "development") {
      console.error("Error validating PRO number:", error);
    }
  }
};

const getHasProValidationProps = (
  hasProNumber: string | undefined,
  isTouched: boolean | undefined,
  errorMsg: string | undefined,
) => {
  const isSelected = HAS_PRO_OPTIONS.includes((hasProNumber ?? "") as any);
  const showValidation = !isSelected && !!isTouched;

  return {
    error: showValidation && !!errorMsg,
    helperText: showValidation ? errorMsg : undefined,
  };
};

const useReportingTerminalValue = (
  reportingTerminal: string | undefined,
  onFieldChange: (field: keyof MisdeliveryFormData, value: any) => void,
) => {
  return useMemo(() => {
    const value = reportingTerminal ?? userTerminal(); /* NOSONAR */
    if (!reportingTerminal && value) {
      setTimeout(() => onFieldChange("reportingTerminal", value), 0);
    }
    return value;
  }, [reportingTerminal, onFieldChange]);
};

const BasicInformationSection: React.FC<BasicInformationSectionProps> = memo(
  () => {
    // Get form state from context
    const {
      formData,
      errors,
      touched,
      onFieldChange,
      onFieldBlur,
      handleBlur,
      disabled,
    } = useMisdeliveryFormContext();

    const [proCheckDError, setProCheckDError] = useState(false);

    // Clear dependent fields
    const clearFields = useCallback(() => {
      FIELDS_TO_CLEAR.forEach((field) => onFieldChange(field, ""));
      if (formData.currentLocationDifferentThanMsdPoint === "N") {
        [
          "currentLocationAddress1",
          "currentLocationAddress2",
          "currentLocationCity",
          "currentLocationZip",
          "currentLocationState",
          "pickupTerminal",
        ].forEach((field) =>
          onFieldChange(field as keyof MisdeliveryFormData, ""),
        );
      }
    }, [onFieldChange, formData.currentLocationDifferentThanMsdPoint]);

    // PRO validation function
    const validateProCheckD = useCallback(
      async (pro: string) => {
        if (!pro.trim()) return;
        await processProValidation(
          pro,
          formData,
          onFieldChange,
          clearFields,
          setProCheckDError,
        );
      },
      [formData, onFieldChange, clearFields],
    );

    // PRO blur handler
    const handleProBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        const proValue = e.target.value.trim();
        onFieldBlur?.("proNumber");

        if (proValue) {
          validateProCheckD(proValue);
        } else {
          clearFields();
          setProCheckDError(false);
        }
      },
      [validateProCheckD, onFieldBlur, clearFields],
    );

    // PRO change handler
    const handleProChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (proCheckDError) {
          setProCheckDError(false);
        }
        onFieldChange("proNumber", e.target.value);
      },
      [proCheckDError, onFieldChange],
    );

    // Memoized computed values
    const proFieldError = useMemo(
      () =>
        getProFieldError(touched.proNumber, errors.proNumber, proCheckDError),
      [touched.proNumber, errors.proNumber, proCheckDError],
    );

    const proFieldHelperText = useMemo(
      () =>
        getProFieldHelperText(
          touched.proNumber,
          errors.proNumber,
          proCheckDError,
        ),
      [proCheckDError, touched.proNumber, errors.proNumber],
    );

    const reportingTerminalValue = useReportingTerminalValue(
      formData.reportingTerminal,
      onFieldChange,
    );

    const handleHasProChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onFieldChange("hasProNumber", e.target.value);
      },
      [onFieldChange],
    );

    const hasProValidation = useMemo(
      () =>
        getHasProValidationProps(
          formData.hasProNumber,
          touched.hasProNumber,
          errors.hasProNumber,
        ),
      [formData.hasProNumber, touched.hasProNumber, errors.hasProNumber],
    );

    return (
      <>
        <Grid container spacing={2} className="filter-margin">
          <FormField
            id="type"
            label="Type"
            value={formData.type}
            required
            disabled={true}
            gridProps={FIELD_GRID_PROPS}
          />
          <FormField
            id="reporting-terminal"
            label="Reporting Terminal"
            value={reportingTerminalValue}
            disabled={true}
            gridProps={FIELD_GRID_PROPS}
          />

          <RadioButtonGroup
            id="hasProNumber"
            label="Is there a PRO # that shipment misdelivered with?"
            value={formData.hasProNumber}
            onChange={handleHasProChange}
            onBlur={handleBlur}
            required
            disabled={disabled}
            error={hasProValidation.error}
            helperText={hasProValidation.helperText}
            gridProps={RADIO_GRID_PROPS}
          />
        </Grid>

        {formData.hasProNumber === "Y" && (
          <Grid container spacing={2} className="filter-margin">
            <FormField
              id="proNumber"
              label="PRO #"
              value={formData.proNumber}
              onChange={handleProChange}
              onBlur={handleProBlur}
              placeholder="Enter PRO Number"
              type="number"
              required
              maxLength={11}
              error={proFieldError}
              helperText={proFieldHelperText}
              gridProps={FIELD_GRID_PROPS}
              disabled={disabled}
            />

            <FormField
              id="originTerminal"
              label="Origin Terminal"
              value={formData.originTerminal}
              disabled
              gridProps={FIELD_GRID_PROPS}
            />

            <FormField
              id="destinationTerminal"
              label="Destination Terminal"
              value={formData.destinationTerminal}
              disabled
              gridProps={FIELD_GRID_PROPS}
            />
            <FormField
              id="lastConfirmedManifest"
              label="Last Confirmed Manifest"
              value={formData.lastConfirmedManifest}
              disabled
              gridProps={FIELD_GRID_PROPS}
            />
          </Grid>
        )}
      </>
    );
  },
);

BasicInformationSection.displayName = "BasicInformationSection";

export default BasicInformationSection;
